import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  X,
  ArrowRight,
  Plus,
  BadgeCheck,
  CheckCircle2,
  ExternalLink,
  MessageCircle,
  AlertCircle,
} from "lucide-react";
import { RootState } from "redux/store";
import FormField from "shared/FormField/FormField";
import PhoneNumberInput from "shared/PhoneNumberInput/PhoneNumberInput";
import API from "utils/helpers/APIHelper";
import __countryListing from "data/jsons/__countryListing.json";
import {
  HOME_UNIVERSITIES,
  ABROAD_UNIVERSITIES,
  modeForValue,
} from "data/universities";

// Swap these for ENDPOINTS.BOOKING_ELIGIBILITY and
// ENDPOINTS.CREATE_EXPERIENCE_BOOKING once you confirm the import path.
const ELIGIBILITY_ENDPOINT = "/api/v1/experience_bookings/eligibility";
const CREATE_BOOKING_ENDPOINT = "/api/v1/experience_bookings";

const whatsappNumber = "51924634308";

// Belongs in the shared options file alongside the university lists.
const NEIGHBORHOODS = [
  "Miraflores",
  "Barranco",
  "San Isidro",
  "Surco",
  "Jesús María",
  "Magdalena",
  "San Miguel",
  "San Borja",
  "Other",
];

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  PEN: "S/",
  EUR: "€",
};

const formatEventDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (timeStr: string | null | undefined): string => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 " +
  "px-4 py-3 text-neutral-900 dark:text-neutral-50 placeholder-neutral-400 dark:placeholder-neutral-500 " +
  "focus:border-[#f5694b] focus:outline-none focus:ring-2 focus:ring-[#f5694b]/30 transition";

const bookingSchema = Yup.object({
  first_name: Yup.string().trim().required("Tell us your first name"),
  last_name: Yup.string().trim().required("Tell us your last name"),
  email: Yup.string()
    .trim()
    .email("Check this email address")
    .required("We need an email to confirm your booking"),
  phone: Yup.string().trim().required("WhatsApp is how we reach you"),
  nationality: Yup.string().trim().required("Tell us where you're from"),
  neighborhood: Yup.string().trim().required("Tell us where you're staying"),
  home_university: Yup.string().trim().required("Tell us your home university"),
  abroad_university: Yup.string()
    .trim()
    .required("Tell us your university in Lima"),
  consent_whatsapp: Yup.boolean().oneOf(
    [true],
    "We need this to send your booking details",
  ),
});

type Step = "identify" | "details" | "payment";

interface BookingResult {
  number: string;
  status: string;
  payment_link: string | null;
  payable: boolean;
  free: boolean;
  duplicate: boolean;
}

interface ExperienceBookingModalProps {
  experience: any;
  isEvent: boolean;
  isFree: boolean;
  displayPrice: string | null | undefined;
  displayCurrency: string;
  onClose: () => void;
}

const ExperienceBookingModal: React.FC<ExperienceBookingModalProps> = ({
  experience,
  isEvent,
  isFree,
  displayPrice,
  displayCurrency,
  onClose,
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);

  // Logged-in students skip the identify step — we already know their email,
  // and their tenancy status is resolved from their token server side.
  const [step, setStep] = useState<Step>(currentUser ? "details" : "identify");
  const [guestEmail, setGuestEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [booking, setBooking] = useState<BookingResult | null>(null);
  const [phoneValidation, setPhoneValidation] = useState({
    isValid: true,
    message: "",
  });

  // Whether this person gets the member rate. Never trusted from the client —
  // the backend resolves it again when it issues the payment link.
  const [isTenant, setIsTenant] = useState(false);

  const [homeUniMode, setHomeUniMode] = useState<"select" | "other">(
    modeForValue(currentUser?.home_university || "", HOME_UNIVERSITIES),
  );
  const [abroadUniMode, setAbroadUniMode] = useState<"select" | "other">(
    modeForValue(currentUser?.abroad_university || "", ABROAD_UNIVERSITIES),
  );
  const [neighborhoodMode, setNeighborhoodMode] = useState<"select" | "other">(
    modeForValue(currentUser?.neighborhood || "", NEIGHBORHOODS),
  );

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // A failed check never blocks the booking — it just means no discount
  // banner, and the payment step resolves the rate again anyway.
  const checkEligibility = useCallback(async (email?: string) => {
    setCheckingEligibility(true);
    try {
      const data: any = await API.post(
        ELIGIBILITY_ENDPOINT,
        email ? { email } : {},
      );
      setIsTenant(Boolean(data?.member));
    } catch {
      setIsTenant(false);
    } finally {
      setCheckingEligibility(false);
    }
  }, []);

  // Signed in: the token identifies them, so check as soon as we open.
  useEffect(() => {
    if (currentUser) checkEligibility();
  }, [currentUser, checkEligibility]);

  const formik = useFormik({
    initialValues: {
      first_name: currentUser?.first_name || "",
      last_name: currentUser?.last_name || "",
      email: currentUser?.email || "",
      phone: currentUser?.phone || "",
      country_code: currentUser?.country_code || "",
      valid_phone_number: "",
      nationality: currentUser?.nationality || "",
      neighborhood: currentUser?.neighborhood || "",
      home_university: currentUser?.home_university || "",
      abroad_university: currentUser?.abroad_university || "",
      notes: "",
      consent_whatsapp: false,
      consent_marketing: false,
    },
    validationSchema: bookingSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setServerError(null);
      try {
        const data: any = await API.post(CREATE_BOOKING_ENDPOINT, {
          experience_id: experience.slug,
          booking: {
            email: values.email,
            first_name: values.first_name,
            last_name: values.last_name,
            phone: values.phone,
            country_code: values.country_code,
            nationality: values.nationality,
            neighborhood: values.neighborhood,
            home_university: values.home_university,
            abroad_university: values.abroad_university,
            notes: values.notes,
            consent_whatsapp: values.consent_whatsapp,
            consent_marketing: values.consent_marketing,
          },
        });

        if (data?.success && data?.booking) {
          setBooking(data.booking);
          setStep("payment");
        } else {
          setServerError(
            data?.error || "That didn't go through. Please try again.",
          );
        }
      } catch (error: any) {
        setServerError(
          error?.response?.data?.error ||
            "That didn't go through. Check your connection and try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleContinue = () => {
    const trimmed = guestEmail.trim();
    if (!trimmed) {
      setEmailError("We need an email to confirm your booking");
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError("Check this email address");
      return;
    }
    setEmailError(null);
    formik.setFieldValue("email", trimmed);
    checkEligibility(trimmed);
    setStep("details");
  };

  const openWhatsApp = () => {
    const verb = isFree ? "registering for" : "booking";
    const ref = booking?.number ? ` (ref ${booking.number})` : "";
    const message = encodeURIComponent(
      `Hi! I'm interested in ${verb} "${experience.title}"${ref}. Could you help me with the payment details?`,
    );
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank");
  };

  const makeSelectHandler =
    (field: string, setMode: (m: "select" | "other") => void) => (e: any) => {
      const val = e?.target?.value ?? e?.value;
      if (val === "Other") {
        setMode("other");
        formik.setFieldValue(field, "");
      } else {
        setMode("select");
        formik.setFieldValue(field, val);
      }
    };

  const handleHomeUniChange = makeSelectHandler(
    "home_university",
    setHomeUniMode,
  );
  const handleAbroadUniChange = makeSelectHandler(
    "abroad_university",
    setAbroadUniMode,
  );
  const handleNeighborhoodChange = makeSelectHandler(
    "neighborhood",
    setNeighborhoodMode,
  );

  const location = experience.venue_name || experience.location;

  const timingLine = isEvent
    ? [formatEventDate(experience.event_date), formatTime(experience.start_time)]
        .filter(Boolean)
        .join(" · ")
    : experience.duration;

  const metaLine = [location, timingLine].filter(Boolean).join(" · ");
  const noun = isEvent ? "event" : "trip";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Book ${experience.title}`}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — experience summary and close, one row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-neutral-700 flex-shrink-0">
          <img
            src={experience.cover_image}
            alt=""
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100 dark:bg-neutral-700"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {experience.title}
            </p>
            {metaLine && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {metaLine}
              </p>
            )}
          </div>
          {isFree ? (
            <span className="text-sm font-bold text-green-500 flex-shrink-0">
              Free
            </span>
          ) : displayPrice ? (
            /* Labelled "From" — this is the lowest configured tier, and the
               rate they actually get depends on the tier they resolve to. */
            <div className="text-right flex-shrink-0">
              <p className="text-[9px] text-gray-400 uppercase tracking-wide leading-none">
                From
              </p>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {CURRENCY_SYMBOLS[displayCurrency] || "$"}
                {parseFloat(displayPrice).toFixed(0)}
              </span>
            </div>
          ) : null}
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 overflow-y-auto">
          {step === "identify" && (
            <div>
              <label
                htmlFor="booking_email"
                className="mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                Your email
              </label>
              <input
                id="booking_email"
                type="email"
                inputMode="email"
                autoFocus
                placeholder="your-email@gmail.com"
                className={inputClass}
                value={guestEmail}
                onChange={(e) => {
                  setGuestEmail(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleContinue();
                }}
              />
              {emailError ? (
                <p className="mt-1.5 text-sm text-red-500 dark:text-red-400">
                  {emailError}
                </p>
              ) : (
                <p className="mt-1.5 text-xs text-neutral-500">
                  We'll use this to send your {noun} confirmation.
                </p>
              )}

              <button
                onClick={handleContinue}
                className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#f5694b] hover:bg-[#e2583d] text-white font-bold rounded-full shadow-lg transition-colors"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Secondary on purpose — the guest path is the default, and
                  friction here costs bookings. The ?book=1 flag is what the
                  experience page reads to reopen this modal after login. */}
              <p className="mt-3 text-center text-xs text-neutral-500">
                Already have an account?{" "}
                <Link
                  to="/login"
                  state={{ from: `/experiences/${experience.slug}?book=1` }}
                  className="font-medium text-[#f5694b] hover:text-[#e2583d]"
                >
                  Log in
                </Link>{" "}
                to skip the form
              </p>
            </div>
          )}

          {step === "details" && (
            <form onSubmit={formik.handleSubmit} noValidate>
              {checkingEligibility ? (
                <div className="mb-3 h-11 rounded-xl bg-gray-100 dark:bg-neutral-700/50 animate-pulse" />
              ) : isTenant ? (
                <div className="mb-3 flex items-start gap-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2.5">
                  <BadgeCheck className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-700 dark:text-green-300 leading-snug">
                    <strong>Nomadroof member discount applied.</strong> You'll
                    see your reduced rate on the payment page.
                  </p>
                </div>
              ) : (
                currentUser && (
                  <p className="mb-3 text-[11px] text-neutral-500 dark:text-neutral-400">
                    Booking as {currentUser.email}
                  </p>
                )
              )}

              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                <FormField
                  label=" * First name"
                  name="first_name"
                  type="text"
                  placeholder="Lena"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.first_name && formik.errors.first_name}
                />
                <FormField
                  label=" * Last name"
                  name="last_name"
                  type="text"
                  placeholder="Fischer"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.last_name && formik.errors.last_name}
                />

                {!currentUser && (
                  <div className="col-span-2">
                    <FormField
                      label=" * Email"
                      name="email"
                      type="email"
                      placeholder="your-email@gmail.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.email && formik.errors.email}
                    />
                  </div>
                )}

                <div className="col-span-2">
                  <PhoneNumberInput
                    label="* WhatsApp number"
                    name="phone"
                    placeholder="Enter a phone number"
                    value={{
                      phone: formik.values.phone,
                      country_code: formik.values.country_code,
                    }}
                    onChange={(value: {
                      phone: string;
                      country_code: string;
                      valid_phone_number: string;
                      isValid: boolean;
                      validationMessage: string;
                    }) => {
                      formik.setFieldValue("phone", value.phone);
                      formik.setFieldValue("country_code", value.country_code);
                      formik.setFieldValue(
                        "valid_phone_number",
                        value.valid_phone_number,
                      );
                      setPhoneValidation({
                        isValid: value.isValid,
                        message: value.validationMessage,
                      });
                    }}
                  />
                  {(!phoneValidation.isValid ||
                    (formik.touched.phone && formik.errors.phone)) && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {phoneValidation.isValid
                        ? (formik.errors.phone as string)
                        : phoneValidation.message}
                    </p>
                  )}
                </div>

                <FormField
                  label=" * Nationality"
                  name="nationality"
                  type="select"
                  placeholder="Select"
                  value={formik.values.nationality}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nationality && formik.errors.nationality}
                  options={__countryListing.map((country) => ({
                    value: country.name,
                    label: country.name,
                  }))}
                />

                <FormField
                  label=" * Where are you staying?"
                  name="neighborhood"
                  type="select"
                  placeholder="Select"
                  value={
                    neighborhoodMode === "other"
                      ? "Other"
                      : formik.values.neighborhood
                  }
                  onChange={handleNeighborhoodChange}
                  onBlur={formik.handleBlur}
                  options={NEIGHBORHOODS.map((n) => ({ value: n, label: n }))}
                  error={
                    neighborhoodMode === "select" &&
                    formik.touched.neighborhood &&
                    formik.errors.neighborhood
                  }
                />

                {neighborhoodMode === "other" && (
                  <div className="col-span-2">
                    <FormField
                      label="Type your neighborhood"
                      name="neighborhood"
                      type="text"
                      placeholder="Neighborhood"
                      value={formik.values.neighborhood}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.neighborhood &&
                        formik.errors.neighborhood
                      }
                    />
                  </div>
                )}

                <FormField
                  label=" * Home university"
                  name="home_university"
                  type="select"
                  placeholder="Select"
                  value={
                    homeUniMode === "other"
                      ? "Other"
                      : formik.values.home_university
                  }
                  onChange={handleHomeUniChange}
                  onBlur={formik.handleBlur}
                  options={HOME_UNIVERSITIES.map((u) => ({
                    value: u,
                    label: u,
                  }))}
                  error={
                    homeUniMode === "select" &&
                    formik.touched.home_university &&
                    formik.errors.home_university
                  }
                />

                <FormField
                  label=" * University in Lima"
                  name="abroad_university"
                  type="select"
                  placeholder="Select"
                  value={
                    abroadUniMode === "other"
                      ? "Other"
                      : formik.values.abroad_university
                  }
                  onChange={handleAbroadUniChange}
                  onBlur={formik.handleBlur}
                  options={ABROAD_UNIVERSITIES.map((u) => ({
                    value: u,
                    label: u,
                  }))}
                  error={
                    abroadUniMode === "select" &&
                    formik.touched.abroad_university &&
                    formik.errors.abroad_university
                  }
                />

                {homeUniMode === "other" && (
                  <div className="col-span-2">
                    <FormField
                      label="Type your home university"
                      name="home_university"
                      type="text"
                      placeholder="Home university"
                      value={formik.values.home_university}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.home_university &&
                        formik.errors.home_university
                      }
                    />
                  </div>
                )}

                {abroadUniMode === "other" && (
                  <div className="col-span-2">
                    <FormField
                      label="Type your university in Lima"
                      name="abroad_university"
                      type="text"
                      placeholder="University in Lima"
                      value={formik.values.abroad_university}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.abroad_university &&
                        formik.errors.abroad_university
                      }
                    />
                  </div>
                )}
              </div>

              {/* Optional note — hidden until asked for */}
              {showNotes ? (
                <div className="mt-2.5">
                  <FormField
                    label="Anything we should know?"
                    name="notes"
                    type="textarea"
                    placeholder="Dietary needs, questions, travelling with a friend…"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowNotes(true)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#f5694b] hover:text-[#e2583d] transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add a note
                </button>
              )}

              {/* Consent */}
              <div className="mt-4 space-y-2.5 pt-3 border-t border-gray-100 dark:border-neutral-700">
                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-neutral-600 dark:text-neutral-400 leading-snug">
                  <input
                    type="checkbox"
                    name="consent_whatsapp"
                    checked={formik.values.consent_whatsapp}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="mt-0.5 h-4 w-4 accent-[#f5694b] flex-shrink-0"
                  />
                  <span>
                    Contact me on WhatsApp and email with my booking details and
                    reminders.
                  </span>
                </label>
                {formik.touched.consent_whatsapp &&
                  formik.errors.consent_whatsapp && (
                    <p className="text-xs text-red-500 dark:text-red-400 ml-6">
                      {formik.errors.consent_whatsapp as string}
                    </p>
                  )}

                <label className="flex cursor-pointer items-start gap-2.5 text-xs text-neutral-600 dark:text-neutral-400 leading-snug">
                  <input
                    type="checkbox"
                    name="consent_marketing"
                    checked={formik.values.consent_marketing}
                    onChange={formik.handleChange}
                    className="mt-0.5 h-4 w-4 accent-[#f5694b] flex-shrink-0"
                  />
                  <span>
                    Also send me upcoming trips, events and offers. Optional —
                    leave any time.
                  </span>
                </label>
              </div>

              {serverError && (
                <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600 dark:text-red-300 leading-snug">
                    {serverError}
                  </p>
                </div>
              )}
            </form>
          )}

          {step === "payment" && booking && (
            <div className="text-center py-2">
              {booking.duplicate && (
                <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-left">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 dark:text-amber-300 leading-snug">
                    You'd already signed up for this {noun} — here's your
                    original booking.
                  </p>
                </div>
              )}

              {/* Already confirmed — nothing left to do */}
              {booking.status === "confirmed" ? (
                <>
                  <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                    You're confirmed
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto leading-relaxed">
                    Your spot on this {noun} is secured. See you there!
                  </p>
                </>
              ) : booking.free ? (
                <>
                  <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                    You're registered
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto leading-relaxed">
                    Nothing to pay. We'll send you a confirmation email shortly
                    — see you there!
                  </p>
                </>
              ) : booking.payable ? (
                <>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                    One last step
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto leading-relaxed">
                    Complete your payment using the link below to confirm your
                    spot — the amount shown there is your rate. Once we've
                    received it you'll get a confirmation email within 24 hours.
                  </p>
                  <a
                    href={booking.payment_link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-[#f5694b] hover:bg-[#e2583d] text-white font-bold rounded-full shadow-lg transition-colors"
                  >
                    Go to payment
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <p className="mt-2.5 text-[11px] text-gray-400">
                    Your spot isn't held until payment is received.
                  </p>
                </>
              ) : (
                /* Tier resolved but no link configured — hand off to a human */
                <>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                    We've got your details
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto leading-relaxed">
                    Message us on WhatsApp and we'll send you the payment
                    details for your rate.
                  </p>
                  <button
                    onClick={openWhatsApp}
                    className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-[#f5694b] hover:bg-[#e2583d] text-white font-bold rounded-full shadow-lg transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Message us on WhatsApp
                  </button>
                </>
              )}

              <div className="mt-5 pt-4 border-t border-gray-100 dark:border-neutral-700">
                <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">
                  Booking reference
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-white tracking-wider">
                  {booking.number}
                </p>
                <p className="mt-1 text-[11px] text-gray-400">
                  Quote this if you message us about your booking.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "details" && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900/40 flex-shrink-0">
            <span className="text-[11px] text-gray-400">No payment yet</span>
            <button
              type="button"
              onClick={() => formik.handleSubmit()}
              disabled={formik.isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#f5694b] hover:bg-[#e2583d] disabled:opacity-60 text-white font-bold rounded-full shadow-lg text-sm transition-colors"
            >
              {formik.isSubmitting
                ? "Sending…"
                : isFree
                  ? "Complete registration"
                  : "Continue to payment"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === "payment" && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900/40 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExperienceBookingModal;